import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { lockBodyScroll } from '../lib/bodyScrollLock.js';
import type { AIModel, Section } from '@volt/shared';
import { getImageModelSpec, IMAGE_MODEL_IDS } from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import { prefs } from '../lib/storage.js';

/** Format bytes to human-readable string (e.g. "30 MB"). */
function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}
import {
  AUTO_MODEL_CHOICE_ID,
  DEFAULT_MODEL_FILTERS,
  clampModelPickerAnchorX,
  hasActiveModelFilters,
  QUICK_MODELS_PREF_KEY,
  filterModels,
  availableProviderCodes,
  getDefaultQuickModelIds,
  modelDifficulty,
  providerLabel,
  sanitizeQuickModelIds,
  type InternetFilter,
  type ModelDifficulty,
} from '../lib/modelPicker.js';
import { Icon } from './Icon.js';
import { Modal } from './Modal.js';
import { useAnalyticsTracking } from '../state/analytics.js';

interface ModelPickerProps {
  open: boolean;
  onClose: () => void;
  models: AIModel[];
  section: Section;
  selectedModelChoice: string;
  resolvedModelId: string | undefined;
  onSelect: (modelId: string) => void;
  /** When provided the panel is anchored to this position (non-chat dropdown). */
  anchor?: { x: number; y: number } | null;
}

const PROVIDERS = [
  { key: 'all', i18nKey: 'composer.providerAll' },
  { key: 'anthropic', i18nKey: 'composer.providerClaude' },
  { key: 'xai', i18nKey: 'composer.providerGrok' },
  { key: 'google', i18nKey: 'composer.providerGemini' },
  { key: 'openai', i18nKey: 'composer.providerGPT' },
  { key: 'moonshot', i18nKey: 'composer.providerKimi' },
  { key: 'deepseek', i18nKey: 'composer.providerDeepSeek' },
  { key: 'microsoft', i18nKey: 'composer.providerMicrosoft' },
] as const;

const DIFFICULTIES: { key: 'all' | ModelDifficulty; i18nKey: string }[] = [
  { key: 'all', i18nKey: 'composer.difficultyAll' },
  { key: 'fast', i18nKey: 'composer.difficulty.fast' },
  { key: 'normal', i18nKey: 'composer.difficulty.normal' },
  { key: 'complex', i18nKey: 'composer.difficulty.complex' },
  { key: 'expert', i18nKey: 'composer.difficulty.expert' },
];

const INTERNET_OPTIONS: { key: InternetFilter; i18nKey: string }[] = [
  { key: 'all', i18nKey: 'composer.internetAll' },
  { key: 'with', i18nKey: 'composer.internetWith' },
  { key: 'without', i18nKey: 'composer.internetWithout' },
];

const DIFFICULTY_LABEL_KEY: Record<ModelDifficulty, string> = {
  fast: 'composer.difficulty.fast',
  normal: 'composer.difficulty.normal',
  complex: 'composer.difficulty.complex',
  expert: 'composer.difficulty.expert',
};

function ProviderAvatar({
  providerCode,
  label,
  size = 'md',
}: {
  providerCode: string;
  label: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  return (
    <span
      className={`model-avatar model-avatar--${providerCode}${size === 'md' ? '' : ` model-avatar--${size}`}`}
      aria-hidden="true"
    >
      {label.trim().charAt(0).toUpperCase()}
    </span>
  );
}

interface ManagerRowProps {
  model: AIModel;
  inQuick: boolean;
  active: boolean;
  onToggle: (modelId: string, inQuick: boolean, unavailable: boolean) => void;
  onInfo: (model: AIModel) => void;
}

const ManagerRow = memo(function ManagerRow({ model, inQuick, active, onToggle, onInfo }: ManagerRowProps) {
  const { t } = useI18n();
  const isUnavailable = !model.isAvailable;
  const difficulty = modelDifficulty(model);
  const toggleLabel = `${inQuick ? t('composer.removeFromQuickList') : t('composer.addToQuickList')}: ${model.displayName}`;
  return (
    <div
      className={`mm-row${inQuick ? ' is-picked' : ''}${active ? ' is-active' : ''}${isUnavailable ? ' is-unavailable' : ''}`}
      role="listitem"
    >
      <button
        type="button"
        className="mm-row-main"
        aria-pressed={inQuick}
        aria-label={toggleLabel}
        title={toggleLabel}
        disabled={isUnavailable && !inQuick}
        onClick={() => onToggle(model.id, inQuick, isUnavailable)}
      >
        <ProviderAvatar providerCode={model.providerCode} label={model.displayName} />
        <span className="mm-row-text">
          <span className="mm-row-head">
            <span className="mm-row-name">{model.displayName}</span>
            {model.supportsWebSearch && (
              <span
                className="mm-row-web"
                role="img"
                aria-label={t('composer.webSearch')}
                title={t('composer.webSearch')}
              >
                <Icon name="globe" size={14} />
              </span>
            )}
          </span>
          <span className="mm-row-tags">
            <span className="mm-tag">{providerLabel(model.providerCode)}</span>
            <span className={`mm-tag mm-tag--level mm-tag--${difficulty}`}>
              {t(DIFFICULTY_LABEL_KEY[difficulty] as Parameters<typeof t>[0])}
            </span>
            {isUnavailable && <span className="mm-tag mm-tag--off">{t('composer.unavailable')}</span>}
          </span>
        </span>
        <span className="mm-toggle" aria-hidden="true">
          <Icon name={inQuick ? 'check' : 'plus'} size={16} />
        </span>
      </button>
      <button
        type="button"
        className="mm-info-btn"
        aria-label={t('composer.modelInfoOpen', { model: model.displayName })}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onInfo(model);
        }}
      >
        <span aria-hidden="true">i</span>
      </button>
    </div>
  );
});

function formatNumber(value: number): string {
  return Number.isFinite(value) ? new Intl.NumberFormat().format(value) : '—';
}

function FeatureRow({ label, enabled: isOn }: { label: string; enabled: boolean | undefined }) {
  return (
    <div className="mm-info-row">
      <span className="mm-info-key">{label}</span>
      <span className={`mm-feature${isOn ? ' is-on' : ''}`}>
        <Icon name={isOn ? 'check' : 'close'} size={14} />
      </span>
    </div>
  );
}

/** Compact floating panel for selecting the active AI model, anchored above the composer. */
export function ModelPicker({
  open,
  onClose,
  models,
  section,
  selectedModelChoice,
  resolvedModelId,
  onSelect,
  anchor,
}: ModelPickerProps) {
  const { t } = useI18n();
  const analytics = useAnalyticsTracking();
  const isChatSection = section === 'chat';
  const [manageOpen, setManageOpen] = useState(false);
  const [query, setQuery] = useState(DEFAULT_MODEL_FILTERS.query);
  const [provider, setProvider] = useState<(typeof PROVIDERS)[number]['key']>(DEFAULT_MODEL_FILTERS.provider);
  const [internet, setInternet] = useState<InternetFilter>(DEFAULT_MODEL_FILTERS.internet);
  const [difficulty, setDifficulty] = useState<'all' | ModelDifficulty>(DEFAULT_MODEL_FILTERS.difficulty);
  const [manageScrollArea, setManageScrollArea] = useState<HTMLElement | null>(null);
  const [infoModel, setInfoModel] = useState<AIModel | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const openedAtRef = useRef<number | null>(null);

  const sectionModels = useMemo(
    () =>
      models.filter((m) => {
        if (m.section !== section || !m.isActive) return false;
        if (section === 'image' && !IMAGE_MODEL_IDS.has(m.id)) return false;
        return true;
      }),
    [models, section],
  );

  const [usedStoredQuickModelIds, setUsedStoredQuickModelIds] = useState(false);
  const [quickModelIds, setQuickModelIds] = useState<string[]>(() => {
    try {
      const stored = prefs.get(QUICK_MODELS_PREF_KEY);
      if (stored) {
        return sanitizeQuickModelIds(JSON.parse(stored) as string[], models);
      }
    } catch {
      // ignore broken local settings
    }
    return sanitizeQuickModelIds(getDefaultQuickModelIds(models), models);
  });

  useEffect(() => {
    try {
      setUsedStoredQuickModelIds(Boolean(prefs.get(QUICK_MODELS_PREF_KEY)));
    } catch {
      setUsedStoredQuickModelIds(false);
    }
  }, []);

  useEffect(() => {
    setQuickModelIds((prev) => {
      if (
        !usedStoredQuickModelIds &&
        (prev.length === 0 || (prev.length === 1 && prev[0] === AUTO_MODEL_CHOICE_ID))
      ) {
        return getDefaultQuickModelIds(models);
      }
      return sanitizeQuickModelIds(prev, models);
    });
  }, [models, usedStoredQuickModelIds]);

  useEffect(() => {
    prefs.set(QUICK_MODELS_PREF_KEY, JSON.stringify(quickModelIds));
  }, [quickModelIds]);

  const resetFilters = useCallback(() => {
    setQuery(DEFAULT_MODEL_FILTERS.query);
    setProvider(DEFAULT_MODEL_FILTERS.provider);
    setInternet(DEFAULT_MODEL_FILTERS.internet);
    setDifficulty(DEFAULT_MODEL_FILTERS.difficulty);
  }, []);

  const bindManageScrollArea = useCallback((node: HTMLDivElement | null) => {
    setManageScrollArea(node);
  }, []);

  useEffect(() => {
    if (!open || anchor) return;
    openedAtRef.current = Date.now();
    const allowTouchMoveWithin = manageOpen ? manageScrollArea : panelRef.current;
    return lockBodyScroll({
      allowTouchMoveWithin: allowTouchMoveWithin instanceof HTMLElement ? allowTouchMoveWithin : null,
    });
  }, [open, manageOpen, manageScrollArea, anchor]);

  useEffect(() => {
    if (!manageOpen || !query.trim()) return;
    const timer = window.setTimeout(() => analytics.trackSearch(), 600);
    return () => window.clearTimeout(timer);
  }, [analytics, manageOpen, query]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (manageOpen) {
          setManageOpen(false);
          return;
        }
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, manageOpen]);

  const quickItems = quickModelIds
    .map((id) => {
      if (id === AUTO_MODEL_CHOICE_ID) {
        return {
          id,
          label: t('composer.autoMode'),
          meta: resolvedModelId
            ? t('composer.autoModeUsing', {
                model: sectionModels.find((item) => item.id === resolvedModelId)?.displayName ?? '',
              })
            : '',
          providerCode: 'auto',
          unavailable: !resolvedModelId,
          active: selectedModelChoice === AUTO_MODEL_CHOICE_ID,
        };
      }
      const model = sectionModels.find((item) => item.id === id);
      if (!model) return null;
      return {
        id,
        label: model.displayName,
        meta: providerLabel(model.providerCode),
        providerCode: model.providerCode,
        unavailable: false,
        active: selectedModelChoice !== AUTO_MODEL_CHOICE_ID && selectedModelChoice === model.id,
      };
    })
    .filter((item): item is NonNullable<typeof item> => Boolean(item));

  const providerChips = useMemo(() => {
    const present = availableProviderCodes(models);
    return PROVIDERS.filter((item) => item.key === 'all' || present.has(item.key));
  }, [models]);
  // A provider whose models stopped working disappears; never leave the list stuck on its empty filter.
  useEffect(() => {
    if (provider !== 'all' && !providerChips.some((item) => item.key === provider)) setProvider('all');
  }, [provider, providerChips]);

  const deferredQuery = useDeferredValue(query);
  const filteredModels = useMemo(
    () =>
      manageOpen
        ? filterModels(models, {
            query: deferredQuery,
            provider,
            internet,
            difficulty,
          })
        : [],
    [manageOpen, models, deferredQuery, provider, internet, difficulty],
  );
  const toggleQuick = useCallback((modelId: string, inQuick: boolean, unavailable: boolean) => {
    if (inQuick) {
      setQuickModelIds((prev) => prev.filter((id) => id !== modelId));
    } else if (!unavailable) {
      setQuickModelIds((prev) => [...prev, modelId]);
    }
  }, []);
  const quickModelIdSet = useMemo(() => new Set(quickModelIds), [quickModelIds]);
  const hasAnyFilters = hasActiveModelFilters({ query, provider, internet, difficulty });

  useEffect(() => {
    if (!open) return;
    analytics.trackModelView(isChatSection ? quickItems.length : sectionModels.length);
  }, [analytics, isChatSection, open, quickItems.length, sectionModels.length]);

  useEffect(() => {
    if (!manageOpen) return;
    analytics.trackModelView(filteredModels.length);
  }, [analytics, filteredModels.length, manageOpen]);

  if (!open) return null;

  // When an anchor is provided (non-chat section), position the panel as a dropdown
  // directly below the trigger button rather than at the fixed bottom-center position.
  const PANEL_MAX_WIDTH = 380;
  const PANEL_VIEWPORT_GAP = 8; // minimum gap from viewport edge
  const anchorStyle: CSSProperties | undefined = anchor
    ? {
        position: 'fixed',
        left: clampModelPickerAnchorX(anchor.x, window.innerWidth, PANEL_MAX_WIDTH, PANEL_VIEWPORT_GAP),
        top: anchor.y,
        bottom: 'auto',
        transform: 'none',
        maxWidth: `calc(100vw - ${2 * PANEL_VIEWPORT_GAP}px)`,
        maxHeight: `min(360px, calc(${window.innerHeight}px - ${anchor.y}px - ${PANEL_VIEWPORT_GAP}px - env(safe-area-inset-bottom, 0px)))`,
      }
    : undefined;

  return (
    <>
      <div
        className="model-panel-overlay"
        onPointerDown={(e) => {
          if (e.target === e.currentTarget) e.preventDefault();
          onClose();
        }}
      >
        <div
          ref={panelRef}
          className={`model-panel${anchor ? ' model-panel--anchored' : ''}`}
          role="menu"
          aria-label={t('composer.selectModel')}
          style={anchorStyle}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="model-panel-list">
            {!isChatSection && sectionModels.length === 0 && <p className="model-empty">{t('common.empty')}</p>}
            {!isChatSection &&
              sectionModels.map((model) => {
                const imgSpec = section === 'image' ? getImageModelSpec(model.id) : undefined;
                const isUnavailable = !model.isAvailable;
                return (
                  <button
                    key={model.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selectedModelChoice === model.id}
                    className={`model-panel-item model-panel-item--rich${selectedModelChoice === model.id ? ' is-active' : ''}${isUnavailable ? ' is-unavailable' : ''}`}
                    disabled={isUnavailable}
                    onClick={() => {
                      analytics.trackModelClick({
                        modelId: model.id,
                        modelName: model.displayName,
                        provider: model.providerCode,
                        section,
                        viewDurationMs:
                          openedAtRef.current == null ? null : Date.now() - openedAtRef.current,
                      });
                      onSelect(model.id);
                      onClose();
                    }}
                  >
                    <div className="model-panel-item-body">
                      <div className="model-panel-item-header">
                        <span className="model-panel-name">{model.displayName}</span>
                        <span className="model-panel-meta">{providerLabel(model.providerCode)}</span>
                        {isUnavailable && <span className="model-unavailable-badge">{t('composer.unavailable')}</span>}
                        {selectedModelChoice === model.id && <Icon name="check" size={16} className="model-check" />}
                      </div>
                      {imgSpec && (
                        <dl className="model-spec-grid">
                          <dt>{t('image.modalities')}</dt>
                          <dd>{imgSpec.modalities.join(', ')}</dd>
                          <dt>{t('image.maxInputImages')}</dt>
                          <dd>{imgSpec.maxInputImages}</dd>
                          <dt>{t('image.inputFormats')}</dt>
                          <dd>{imgSpec.inputFormats.join(', ')}</dd>
                          <dt>{t('image.imageSizeMax')}</dt>
                          <dd>{formatBytes(imgSpec.imageSizeMaxBytes)}</dd>
                        </dl>
                      )}
                    </div>
                  </button>
                );
              })}
            {isChatSection && quickItems.length === 0 && <p className="model-empty">{t('common.empty')}</p>}
            {isChatSection && quickItems.map((item) => (
              <button
                key={item.id}
                type="button"
                role="menuitemradio"
                aria-checked={item.active}
                className={`model-panel-item${item.active ? ' is-active' : ''}${item.unavailable ? ' is-unavailable' : ''}`}
                onClick={() => {
                  if (!item.unavailable) {
                    const model =
                      item.id === AUTO_MODEL_CHOICE_ID
                        ? sectionModels.find((candidate) => candidate.id === resolvedModelId)
                        : sectionModels.find((candidate) => candidate.id === item.id);
                    if (model) {
                      analytics.trackModelClick({
                        modelId: model.id,
                        modelName: model.displayName,
                        provider: model.providerCode,
                        section,
                        viewDurationMs:
                          openedAtRef.current == null ? null : Date.now() - openedAtRef.current,
                      });
                    }
                    onSelect(item.id);
                    onClose();
                  }
                }}
                disabled={item.unavailable}
              >
                <ProviderAvatar providerCode={item.providerCode} label={item.label} />
                <span className="model-panel-text">
                  <span className="model-panel-name">{item.label}</span>
                  <span className="model-panel-meta">{item.meta}</span>
                </span>
                {item.active && <Icon name="check" size={16} className="model-check" />}
              </button>
            ))}
          </div>
          {isChatSection && (
            <div className="model-panel-foot">
              <button
                type="button"
                className="model-panel-manage-btn"
                onClick={() => setManageOpen(true)}
              >
                {t('composer.manageModels')}
              </button>
            </div>
          )}
        </div>
      </div>

      <Modal
        open={isChatSection && manageOpen}
        onClose={() => {
          if (infoModel) {
            setInfoModel(null);
            return;
          }
          setManageOpen(false);
          setInfoModel(null);
          onClose();
        }}
        title={infoModel ? t('composer.modelInfo') : t('composer.manageModels')}
        closeLabel={t('common.close')}
        className="modal--manage"
      >
        {infoModel ? (
          <div ref={bindManageScrollArea} className="mm-info">
            <button type="button" className="mm-back" onClick={() => setInfoModel(null)}>
              <Icon name="back" size={16} />
              {t('common.back')}
            </button>

            <div className="mm-info-hero">
              <ProviderAvatar providerCode={infoModel.providerCode} label={infoModel.displayName} size="lg" />
              <div className="mm-info-hero-text">
                <h3 className="mm-info-title">{infoModel.displayName}</h3>
                <p className="mm-info-sub">
                  {providerLabel(infoModel.providerCode)} ·{' '}
                  {t(DIFFICULTY_LABEL_KEY[modelDifficulty(infoModel)] as Parameters<typeof t>[0])}
                </p>
              </div>
            </div>

            <div className="mm-stats">
              <div className="mm-stat">
                <span className="mm-stat-value">{formatNumber(infoModel.contextLimit)}</span>
                <span className="mm-stat-label">{t('composer.modelInfoContextLimit')}</span>
              </div>
              <div className="mm-stat">
                <span className="mm-stat-value">{formatNumber(infoModel.maxOutputTokens)}</span>
                <span className="mm-stat-label">{t('composer.modelInfoMaxOutput')}</span>
              </div>
            </div>

            <div className="mm-info-group">
              <div className="mm-info-row">
                <span className="mm-info-key">{t('composer.modelInfoProvider')}</span>
                <span className="mm-info-val">{providerLabel(infoModel.providerCode)}</span>
              </div>
              <div className="mm-info-row">
                <span className="mm-info-key">{t('composer.modelInfoSection')}</span>
                <span className="mm-info-val">
                  {t((`section.${infoModel.section}`) as Parameters<typeof t>[0])}
                </span>
              </div>
              <div className="mm-info-row">
                <span className="mm-info-key">{t('composer.modelInfoModality')}</span>
                <span className="mm-info-val">{infoModel.modality}</span>
              </div>
              <div className="mm-info-row">
                <span className="mm-info-key">{t('composer.modelInfoPricingUnit')}</span>
                <span className="mm-info-val">{infoModel.pricingUnit}</span>
              </div>
            </div>

            <div className="mm-info-group">
              <FeatureRow label={t('composer.modelInfoWebSearch')} enabled={infoModel.supportsWebSearch} />
              <FeatureRow label={t('composer.modelInfoStreaming')} enabled={infoModel.supportsStreaming} />
              <FeatureRow label={t('composer.modelInfoVision')} enabled={infoModel.supportsVision} />
            </div>

            {Object.keys(infoModel.price).length > 0 && (
              <div className="mm-info-group">
                {Object.entries(infoModel.price).map(([key, value]) => (
                  <div key={key} className="mm-info-row">
                    <span className="mm-info-key">{key}</span>
                    <span className="mm-info-val mm-info-val--mono">
                      {typeof value === 'number' ? value.toString() : '-'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="mm">
            <div className="mm-top">
              <div className="mm-search">
                <Icon name="search" size={18} className="mm-search-icon" />
                <input
                  type="search"
                  className="mm-search-input"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t('composer.searchModels')}
                  aria-label={t('composer.searchModels')}
                />
                {query && (
                  <button
                    type="button"
                    className="mm-search-clear"
                    aria-label={t('composer.clearSearch')}
                    onClick={() => setQuery('')}
                  >
                    <Icon name="close" size={12} />
                  </button>
                )}
              </div>

              <div className="mm-scroll-row" role="group" aria-label={t('composer.modelInfoProvider')}>
                {providerChips.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`mm-chip${provider === item.key ? ' is-active' : ''}`}
                    aria-pressed={provider === item.key}
                    onClick={() => setProvider(item.key)}
                  >
                    {t(item.i18nKey)}
                  </button>
                ))}
              </div>

              <div className="mm-scroll-row">
                <div className="mm-seg" role="group">
                  {DIFFICULTIES.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      className={`mm-seg-btn${difficulty === item.key ? ' is-active' : ''}`}
                      aria-pressed={difficulty === item.key}
                      onClick={() => setDifficulty(item.key)}
                    >
                      {t(item.i18nKey as Parameters<typeof t>[0])}
                    </button>
                  ))}
                </div>
                <div
                  className="mm-seg mm-seg--web"
                  role="group"
                  aria-label={t('composer.webSearch')}
                  title={t('composer.webSearch')}
                >
                  {INTERNET_OPTIONS.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      className={`mm-seg-btn${internet === item.key ? ' is-active' : ''}`}
                      aria-pressed={internet === item.key}
                      onClick={() => setInternet(item.key)}
                    >
                      {item.key === 'with' && <Icon name="globe" size={14} />}
                      {t(item.i18nKey as Parameters<typeof t>[0])}
                    </button>
                  ))}
                </div>
              </div>

              {quickItems.length > 0 && (
                <section className="mm-quick" aria-label={t('composer.quickList')}>
                  <div className="mm-section-head">
                    <h3 className="mm-eyebrow">{t('composer.quickList')}</h3>
                    <span className="mm-count">{quickItems.length}</span>
                  </div>
                  <div className="mm-quick-row">
                    {quickItems.map((item) => (
                      <div key={item.id} className={`mm-qchip${item.active ? ' is-active' : ''}`}>
                        <ProviderAvatar providerCode={item.providerCode} label={item.label} size="sm" />
                        <span className="mm-qchip-name">{item.label}</span>
                        {item.id !== AUTO_MODEL_CHOICE_ID && (
                          <button
                            type="button"
                            className="mm-qchip-remove"
                            aria-label={`${t('composer.removeFromQuickList')}: ${item.label}`}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              setQuickModelIds((prev) => prev.filter((id) => id !== item.id));
                            }}
                          >
                            <Icon name="close" size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <div className="mm-list-head">
                <span className="mm-count">{t('composer.modelsCount', { count: filteredModels.length })}</span>
                {hasAnyFilters && filteredModels.length > 0 && (
                  <button type="button" className="mm-link" onClick={resetFilters}>
                    {t('composer.resetFilters')}
                  </button>
                )}
              </div>
            </div>

            <div ref={bindManageScrollArea} className="mm-list" role="list">
              {filteredModels.length === 0 && (
                <div className="mm-empty">
                  <span className="mm-empty-icon" aria-hidden="true">
                    <Icon name="search" size={22} />
                  </span>
                  <p className="mm-empty-text">{t('composer.noModelsForFilters')}</p>
                  {hasAnyFilters && (
                    <button type="button" className="mm-pill-btn" onClick={resetFilters}>
                      {t('composer.resetFilters')}
                    </button>
                  )}
                </div>
              )}
              {filteredModels.map((model) => (
                <ManagerRow
                  key={model.id}
                  model={model}
                  inQuick={quickModelIdSet.has(model.id)}
                  active={selectedModelChoice === model.id}
                  onToggle={toggleQuick}
                  onInfo={setInfoModel}
                />
              ))}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
